/* Buta-8 basket quiz — food table and fibre-reach model.
   Split out of snippets/buta8-basket.liquid on 2026-09-22. The snippet was 989 lines of markup,
   CSS, behaviour and data in one file; adding a reach dimension would have pushed it past 1,400.
   Data lives here so the food table can be audited without reading CSS.

   ---------------------------------------------------------------------------------------------
   THE REACH MODEL, and why the numbers are what they are.

   Fibre grams are not the interesting variable. A 29 g day and a 29 g day can feed completely
   different stretches of colon, and the far end - the stretch fed least, where contents sit
   longest - is the one that misses out. So every food carries a fibre CLASS, and the class
   carries a fermentation speed. Nine tags, four speeds: that is reviewable in one sitting,
   which eighty per-food judgement calls would not be.

   Profiles are reasoned from measured fermentation KINETICS, not from solubility. The binary
   gets this backwards - inulin and psyllium are both "soluble fibre" on a label and behave
   nothing alike.

   Primary source for the speeds: Kaur A, Rose DJ, Rumpagaporn P, Patterson JA, Hamaker BR.
   "In vitro batch fecal fermentation comparison of gas and short-chain fatty acid production
   using 'slowly fermentable' dietary fibers." J Food Sci 2011. PMID 22417432. Human faecal
   inoculum, 48 h, gas and SCFA measured throughout. Its findings, verbatim in substance:

     - FOS is the fast control.
     - Corn bran arabinoxylan and long-chain beta-glucan "initially appeared slower fermenting
       ... but later fermented rapidly with little remaining in the final half."  So they are
       DELAYED, not sustained. An earlier draft of this model had arabinoxylan as the one food
       fibre reaching the distal colon. That was wrong.
     - Long-chain inulin and psyllium: "slow and moderate, but incomplete" fermentation.
     - The resistant fraction of cooked and cooled potato starch "fermented rapidly and appeared
       similar to FOS."
     - Overall: "only the starch-entrapped microspheres appreciably fermented in the second half"
       - and those are an engineered material, not a food.

   Corroborating: Morita T, Kasaoka S, Hase K, Kiriyama S, J Nutr 1999;129:2081-2087 (PMID
   10539788) - in rats on high-amylose starch, butyrate FELL along the colon's length, faecal
   concentration reaching one-third of caecal. Resistant starch lights up the caecum and runs out.

   The honest consequence, and the reason this quiz exists: almost nothing on an ordinary plate
   ferments in the second half. That is a real finding, not a sales line, and it is what the
   result section is allowed to say.

   TWO THINGS THIS MODEL IS NOT.
   1. It is not a measurement. These are coarse, reasoned profiles at class level, tagged so a
      reviewer can disagree with a specific number. Treat as the science page treats its own
      relay chart: conceptual model, illustrative.
   2. Food RS is not the same as the RS in Buta-8. Cooked-and-cooled food starch (RS1/RS3) is
      fast, per Kaur. The raw granular high-amylose RS2 in a supplement is slower - which is why
      the science page rates its own resistant starch "moderate" while this table rates FOOD
      resistant starch fast. Same words, different material. Do not let the two tables be read
      as contradicting each other.
   --------------------------------------------------------------------------------------------- */

(function (w) {
  "use strict";

  /* SPEED, not a four-band ratio.
     The first version of this model gave every class a four-way early/mid/late/bulk split and
     judged a day on how much landed past the caecum as a share of FERMENTABLE fibre. It scored
     every test day the same, and the reason was instructive: cellulose barely ferments, but the
     sliver that does ferments late - so normalising by fermentable fibre handed reach credit to
     the one class that contributes least. The metric rewarded the wrong thing.

     This version keys off the property Kaur 2011 actually measured - how fast each fibre is
     fermented - and nothing else. Four speeds, every assignment traceable to a sentence:

       fast     FOS is the study's fast control. Pectin is fast and near-complete (Titgemeyer,
                Yu, via the science page). GOS is an oligosaccharide and behaves likewise.
                Cooked-and-cooled potato RS "fermented rapidly and appeared similar to FOS".
       delayed  Arabinoxylan and beta-glucan "initially appeared slower ... but later fermented
                rapidly with little remaining in the final half." Late to start, not sustained.
       slow     The psyllium family: "slow and moderate, but incomplete". On this menu that is
                mucilage only - chia and okra. The one food-side class that reaches late at all.
       bulk     Cellulose and lignin. Travel the whole length, ferment next to nothing. Mass,
                not fuel, and never counted as reach.

     Position along the colon follows from speed, which is why one model serves both the verdict
     and the picture: fast burns out in the caecum, delayed carries into the transverse, slow is
     the only thing still working at the far end, bulk passes through without feeding anything. */
  var SPEED = {
    fructan:    { label: "Fructan (inulin / FOS)", speed: "fast" },
    pectin:     { label: "Pectin",                 speed: "fast" },
    gos:        { label: "GOS",                    speed: "fast" },
    resStarch:  { label: "Resistant starch",       speed: "fast" },
    betaGlucan: { label: "Beta-glucan",            speed: "delayed" },
    arabinoxyl: { label: "Arabinoxylan",           speed: "delayed" },
    mucilage:   { label: "Mucilage",               speed: "slow" },
    cellulose:  { label: "Cellulose",              speed: "bulk" },
    lignin:     { label: "Lignin",                 speed: "bulk" }
  };

  /* A day reads BROAD when a third or more of its FERMENTABLE fibre is delayed or slow.
     Judgement call, set once, here. Keyed to slow alone almost nobody would clear it and the
     verdict would be a foregone conclusion - dishonest in the other direction. At a third, an
     ordinary Indian day of rice, dal and one roti comes out narrow, which is the true answer,
     and a day built with oats, chia, okra or brown rice clears it, which is also true. */
  var BROAD_AT = 0.35;

  // name | serving | grams | fibre g | kcal | fibre classes, weights summing to 1
  // Fibre and kcal are unchanged, USDA-derived, provenance in Buta8_Basket_Data_Provenance.md.
  // Class weights are reasoned from the assumed recipe beside each in the original table.
  var MEALS = [
    { name: "Breakfast", note: "Most Indian breakfasts land between 1 and 5 g.",
      didT: "The cells lining your colon don't run on blood sugar. They burn butyrate, a short-chain fatty acid your gut bacteria make out of fibre, for most of their energy.",
      didC: "Roediger, Gut, 1980",
      items: [
        ["Poha", "1 plate", 150, 1.5, 249, { resStarch: .50, fructan: .20, cellulose: .30 }],
        ["Upma", "1 plate", 150, 2.3, 234, { arabinoxyl: .45, fructan: .15, pectin: .20, cellulose: .20 }],
        ["Idli", "2", 120, 2.2, 144, { resStarch: .45, gos: .45, cellulose: .10 }],
        ["Dosa", "1", 120, 2.2, 179, { resStarch: .45, gos: .45, cellulose: .10 }],
        ["Paratha with curd", "1", 160, 4.8, 261, { arabinoxyl: .50, cellulose: .40, fructan: .10 }],
        ["Bread toast", "2 slices", 50, 1.2, 132, { arabinoxyl: .50, cellulose: .40, fructan: .10 }],
        ["Oats", "40 g dry", 40, 4.0, 152, { betaGlucan: .55, arabinoxyl: .20, cellulose: .25 }],
        ["Chia seeds", "1 tbsp", 12, 4.1, 59, { mucilage: .35, cellulose: .45, lignin: .20 }],
        ["Banana", "1 medium", 120, 3.1, 105, { pectin: .40, resStarch: .35, fructan: .25 }],
        ["Chai with sugar", "1 cup", 150, 0, 90, {}]
      ],
      noteUS: "Most Western breakfasts land between 1 and 6 g.",
      itemsUS: [
        ["Oatmeal", "1 cup cooked", 234, 4, 166, { betaGlucan: .55, arabinoxyl: .20, cellulose: .25 }],
        ["Bagel, plain", "1 medium", 105, 2.3, 289, { arabinoxyl: .45, cellulose: .55 }],
        ["Whole-wheat toast", "2 slices", 56, 3.8, 138, { arabinoxyl: .45, cellulose: .45, fructan: .10 }],
        ["White toast", "2 slices", 50, 1.4, 133, { arabinoxyl: .45, cellulose: .55 }],
        ["Scrambled eggs", "2", 100, 0, 148, {}],
        ["Greek yoghurt, plain", "170 g", 170, 0, 100, {}],
        ["Granola", "1/2 cup", 55, 3.5, 250, { betaGlucan: .30, cellulose: .40, lignin: .30 }],
        ["Blueberries", "1 cup", 148, 3.6, 84, { pectin: .45, cellulose: .35, lignin: .20 }],
        ["Banana", "1 medium", 118, 3.1, 105, { pectin: .40, resStarch: .35, fructan: .25 }],
        ["Peanut butter", "2 tbsp", 32, 1.9, 188, { cellulose: .60, lignin: .40 }],
        ["Coffee with milk", "1 cup", 240, 0, 30, {}]
      ] },
    { name: "Lunch", note: "A typical thali lunch lands between 4 and 8 g.",
      didT: "Fibres don't ferment at the same speed. Psyllium eaten alongside inulin slows inulin's fermentation down, and the pair produces less gas than inulin does on its own.",
      didC: "Gunn et al., Gut, 2022",
      items: [
        ["White rice", "1 katori", 100, 0.4, 130, { resStarch: .70, cellulose: .30 }],
        ["Brown rice", "1 katori", 100, 1.8, 112, { arabinoxyl: .35, resStarch: .30, cellulose: .35 }],
        ["Wheat roti", "1", 40, 2.5, 110, { arabinoxyl: .45, cellulose: .45, fructan: .10 }],
        ["Maida paratha", "1", 60, 1.1, 180, { arabinoxyl: .40, cellulose: .50, fructan: .10 }],
        ["Dal", "1 katori", 100, 4.0, 116, { gos: .45, resStarch: .20, cellulose: .35 }],
        ["Rajma", "1 katori", 100, 6.4, 125, { gos: .40, resStarch: .25, cellulose: .35 }],
        ["Sambar", "1 katori", 100, 2.2, 85, { gos: .35, pectin: .25, cellulose: .40 }],
        ["Paneer sabzi", "1 katori", 100, 0.5, 200, { cellulose: .60, pectin: .40 }],
        ["Aloo sabzi", "1 katori", 100, 1.8, 120, { resStarch: .40, pectin: .20, cellulose: .40 }],
        ["Mixed salad", "1 bowl", 150, 2.2, 35, { cellulose: .60, pectin: .30, lignin: .10 }]
      ],
      noteUS: "A typical Western lunch lands between 3 and 9 g.",
      itemsUS: [
        ["Turkey sandwich, wholemeal", "1", 200, 4, 350, { arabinoxyl: .40, cellulose: .45, pectin: .15 }],
        ["Chicken caesar salad", "1 bowl", 250, 3, 400, { cellulose: .75, pectin: .25 }],
        ["Black bean burrito", "1", 250, 9, 450, { gos: .40, resStarch: .25, cellulose: .35 }],
        ["Quinoa bowl", "1 cup cooked", 185, 5.2, 222, { arabinoxyl: .30, resStarch: .25, cellulose: .45 }],
        ["Lentil soup", "1 cup", 240, 7.8, 180, { gos: .45, resStarch: .20, cellulose: .35 }],
        ["Brown rice", "1 cup cooked", 195, 3.5, 218, { arabinoxyl: .35, resStarch: .30, cellulose: .35 }],
        ["White rice", "1 cup cooked", 186, 0.6, 242, { resStarch: .70, cellulose: .30 }],
        ["Baked potato, with skin", "1 medium", 173, 3.8, 161, { resStarch: .40, pectin: .15, cellulose: .45 }],
        ["Avocado", "half", 68, 4.6, 114, { cellulose: .65, pectin: .35 }],
        ["Green salad", "1 bowl", 150, 2, 35, { cellulose: .70, pectin: .30 }]
      ] },
    { name: "Snack", note: "Snacks add 0 to 3 g, unless fruit is involved.",
      didT: "Resistant starch mostly passes through unfermented unless one particular bacterium is present to open it up: Ruminococcus bromii. Who lives in your colon decides what your fibre is worth.",
      didC: "Ze et al., ISME Journal, 2012",
      items: [
        ["Biscuits", "3", 30, 0.6, 140, { arabinoxyl: .40, cellulose: .60 }],
        ["Namkeen", "1 small bowl", 30, 1.0, 160, { gos: .30, arabinoxyl: .20, cellulose: .50 }],
        ["Chai with sugar", "1 cup", 150, 0, 90, {}],
        ["Makhana", "1 bowl", 25, 3.5, 87, { resStarch: .45, cellulose: .55 }],
        // Guava's fibre is famously seed-heavy, and seed is lignin: it reaches the far end and
        // ferments nothing. Tagging all 8 g as pectin would credit reach the fruit does not have.
        ["Guava", "1 medium", 150, 8.0, 102, { pectin: .35, lignin: .30, cellulose: .35 }],
        ["Apple, with skin", "1 medium", 180, 4.4, 95, { pectin: .45, cellulose: .45, lignin: .10 }],
        ["Roasted chana", "30 g", 30, 5.1, 116, { gos: .40, resStarch: .20, cellulose: .40 }],
        ["Almonds", "about 15", 20, 2.5, 116, { cellulose: .50, lignin: .30, arabinoxyl: .20 }]
      ],
      noteUS: "Snacks add 0 to 4 g, unless fruit or nuts are involved.",
      itemsUS: [
        ["Almonds", "1 oz", 28, 3.5, 164, { cellulose: .50, lignin: .30, arabinoxyl: .20 }],
        ["Apple, with skin", "1 medium", 182, 4.4, 95, { pectin: .45, cellulose: .45, lignin: .10 }],
        ["Potato crisps", "1 oz", 28, 1.2, 152, { resStarch: .35, cellulose: .65 }],
        ["Popcorn, air-popped", "3 cups", 24, 3.5, 93, { arabinoxyl: .30, cellulose: .50, lignin: .20 }],
        ["Protein bar", "1", 60, 3, 220, { fructan: .40, cellulose: .60 }],
        ["Hummus with carrots", "1 small plate", 100, 4, 180, { gos: .40, pectin: .25, cellulose: .35 }],
        ["Chia pudding", "1 pot", 150, 8, 180, { mucilage: .35, cellulose: .45, lignin: .20 }],
        ["Dark chocolate", "1 oz", 28, 3.1, 170, { cellulose: .55, lignin: .45 }]
      ] },
    { name: "Dinner", note: "Dinner usually adds 4 to 8 g.",
      didT: "People eating more than thirty distinct plant types a week carry more diverse gut bacteria than people eating ten or fewer. That is a better predictor than whether they call themselves vegetarian or omnivore.",
      didC: "McDonald et al., American Gut Project, mSystems, 2018",
      items: [
        ["White rice", "1 katori", 100, 0.4, 130, { resStarch: .70, cellulose: .30 }],
        ["Wheat roti", "1", 40, 2.5, 110, { arabinoxyl: .45, cellulose: .45, fructan: .10 }],
        ["Khichdi", "1 katori", 150, 2.4, 180, { resStarch: .40, gos: .35, cellulose: .25 }],
        ["Masoor dal", "1 katori", 100, 4.0, 116, { gos: .45, resStarch: .20, cellulose: .35 }],
        ["Chole", "1 katori", 100, 7.6, 164, { gos: .40, resStarch: .25, cellulose: .35 }],
        ["Paneer sabzi", "1 katori", 100, 0.5, 200, { cellulose: .60, pectin: .40 }],
        // Okra is mucilaginous. It and chia are the only things on this menu in the psyllium
        // family - the one food-side class that reaches late at all.
        ["Bhindi", "1 katori", 100, 3.2, 36, { mucilage: .30, pectin: .25, cellulose: .45 }],
        ["Spinach", "1 katori", 100, 2.4, 23, { cellulose: .75, pectin: .25 }],
        ["Cauliflower, roasted", "1 katori", 100, 2.0, 30, { cellulose: .65, pectin: .35 }],
        ["Mixed veg", "1 katori", 100, 2.8, 60, { cellulose: .60, pectin: .30, lignin: .10 }]
      ],
      noteUS: "Dinner usually adds 4 to 10 g.",
      itemsUS: [
        ["Grilled chicken", "1 breast", 150, 0, 250, {}],
        ["Salmon", "1 fillet", 150, 0, 280, {}],
        ["Pasta, white", "1 cup cooked", 140, 2.5, 220, { arabinoxyl: .35, resStarch: .30, cellulose: .35 }],
        ["Wholewheat pasta", "1 cup cooked", 140, 6.3, 174, { arabinoxyl: .45, cellulose: .45, fructan: .10 }],
        ["Broccoli, steamed", "1 cup", 156, 5.1, 55, { cellulose: .65, pectin: .35 }],
        ["Sweet potato, baked", "1 medium", 130, 3.8, 112, { pectin: .35, resStarch: .25, cellulose: .40 }],
        ["Black beans", "1/2 cup", 86, 7.5, 114, { gos: .40, resStarch: .25, cellulose: .35 }],
        ["Brussels sprouts", "1 cup", 156, 4.1, 56, { cellulose: .65, pectin: .35 }],
        ["Garlic bread", "1 slice", 40, 1.2, 150, { arabinoxyl: .35, fructan: .20, cellulose: .45 }],
        ["Green salad", "1 bowl", 150, 2, 35, { cellulose: .70, pectin: .30 }]
      ] }
  ];

  w.B8_BASKET = { SPEED: SPEED, MEALS: MEALS, BROAD_AT: BROAD_AT };
}(window));
